import { getAbTestEligibility, AB_CONSTANTS } from 'dibs-cookie-jar/exports/abTestHelpers';
import { AB_TYPES, getConfig, AB_TYPE } from './sharedABTestHelpers';
import { experimentConfigProps } from './types/abTesting';
import serverVars from 'server-vars';

import { ABTestData } from 'dibs-tracking';

type Cookie = string | Record<string, string>;
type ABReturnTypes = AB_TYPE | string; // valid string: 'VARIANT 1', 'VARIANT 2', 'VARIANT n'

function isVariantInRange(value: number, allocation: string): boolean {
    const [min, max] = allocation.split('-');
    return parseInt(min) <= value && value <= parseInt(max);
}

/**
 * Compares 00-99 variant with allocation (00-99) range from config. If variant is in allocation range, user is in experiment VARIANT
 */
const _getExperimentStatusForConfig = (
    cookie: Cookie,
    experimentConfig?: experimentConfigProps
): ABReturnTypes => {
    if (!experimentConfig) {
        return AB_TYPES.INELIGIBLE;
    }
    const { testGroup: targetGroup, allocation } = experimentConfig;
    const { group: cookieGroup, eligibility } = getAbTestEligibility(cookie);
    const currentAllocation = parseInt(eligibility);
    const allocations = allocation
        .split(',') // ['00-99'] or ['00-N1',..,'NN-99']
        .map((allocationRange, index) => ({ allocationRange, index: index + 1 }));

    if (allocations.length === 0) {
        return AB_TYPES.INELIGIBLE;
    }

    const isControlGroup = cookieGroup === AB_CONSTANTS.SHARED_CONTROL_GROUP;
    const isSingleVariateGroup = cookieGroup === targetGroup && allocations.length === 1;
    const isMultivariateGroup = cookieGroup === targetGroup && allocations.length > 1;
    const isFirstAllocationInRange = isVariantInRange(
        currentAllocation,
        allocations[0].allocationRange
    );

    if (isControlGroup) {
        return isFirstAllocationInRange ? AB_TYPES.CONTROL : AB_TYPES.INELIGIBLE;
    } else if (isSingleVariateGroup) {
        return isFirstAllocationInRange ? AB_TYPES.VARIANT : AB_TYPES.INELIGIBLE;
    } else if (isMultivariateGroup) {
        const testVariant = allocations.find(o =>
            isVariantInRange(currentAllocation, o.allocationRange)
        );
        return testVariant ? `${AB_TYPES.VARIANT} ${testVariant.index}` : AB_TYPES.CONTROL;
    }

    return AB_TYPES.INELIGIBLE;
};

/**
 * Return the test status for given experiment name by inspecting ab test cookie
 */
const getAbTestStatus = (cookie: Cookie, experimentName: string): ABReturnTypes => {
    const experimentConfig = getConfig(serverVars)[experimentName];
    return _getExperimentStatusForConfig(cookie, experimentConfig);
};

const isAbTestVariant = (cookie: Cookie, experimentName: string): boolean =>
    getAbTestStatus(cookie, experimentName).includes(AB_TYPES.VARIANT);

const isAbTestControl = (cookie: Cookie, experimentName: string): boolean =>
    getAbTestStatus(cookie, experimentName) === AB_TYPES.CONTROL;

const getMultivariateTestStatus = (cookie: Cookie, experimentName: string): string => {
    return getAbTestStatus(cookie, experimentName);
};

const getAbTestTrackingData = (
    cookie: Cookie,
    experimentName: string,
    additionalTrackingPrefix: string | null = null
): ABTestData => {
    const experimentConfig = getConfig(serverVars)[experimentName];
    const status = getAbTestStatus(cookie, experimentName);
    let trackingData: ABTestData = {};
    if (experimentConfig && experimentConfig.testGroup && status !== AB_TYPES.INELIGIBLE) {
        let testGroupValue = `${experimentName} - ${status}`;
        if (additionalTrackingPrefix) {
            testGroupValue = `${additionalTrackingPrefix} - ${testGroupValue}`;
        }
        trackingData = {
            testGroup: status,
            additional: {
                [experimentConfig.testGroup]: testGroupValue,
                cdnTestConfigType: experimentConfig.testType.toLowerCase(),
            },
        };
    }
    return trackingData;
};

export { isAbTestVariant, isAbTestControl, getAbTestTrackingData, getMultivariateTestStatus };
